/**
 * The permission the backend actually enforces. `RequireAdmin()` compares the
 * user's role against `super_admin` and nothing else, so this list mirrors it
 * one to one. Inventing finer-grained permissions here would be the client
 * recomputing access rules the server owns.
 */
export const PERMISSION_ADMIN = 'admin'

export type UserRole = 'user' | 'super_admin'

/**
 * One subscription — either the running one or the one paid for and waiting.
 *
 * The plan carries both names rather than one already chosen: the panel picks by
 * the reader's language, and the session is cached — resolving the name at fetch
 * time would freeze it to whichever language was current when the session was
 * first read, and switching language would not change it back.
 */
export type Subscription = {
  status: string
  planSlug: string
  planNameRu: string
  planNameEn: string
  tier: number
  /** null = unlimited. Comes with the subscription, so nothing recomputes it. */
  maxCampaigns: number | null
  /** Rounded up by the server: "1 day left" while it still works, never "0". */
  daysLeft: number
  currentPeriodEnd?: string
}

export type Session = {
  id: string
  name: string
  email: string
  role: UserRole
  /** Reported by the server and rendered. Never recomputed on the client. */
  permissions: string[]
  subscription: Subscription | null
  /**
   * Paid for and not started. A downgrade cannot take effect while the larger
   * plan still has days left, so it waits — and the panel has to say so, or the
   * money looks like it went nowhere.
   */
  scheduledSubscription: Subscription | null
  /**
   * Comes from the token response, not from `/me`. goauth enforces nothing on
   * it, so the guard in `_auth.tsx` is the whole of the rule.
   */
  mustChangePassword: boolean
}

export const permissionsForRole = (role: UserRole): string[] =>
  role === 'super_admin' ? [PERMISSION_ADMIN] : []
